import {useEffect, useState} from "react";
import {AlertTriangle, Info} from "lucide-react";
import {getDetailBenefitProbabilitySegments} from "../../api/project/project.api";

/**
 * 확률 변경 구간별 발급 현황.
 *
 * 기존 '혜택 성과'는 "현재 확률 × 전 구간 누적 발급수"만 보여주기 때문에,
 * 이벤트 진행 중 확률을 조정한 프로젝트는 반드시 "확률대로 안 나온다"처럼 보인다.
 * 이 컴포넌트는 benefit_result.current_percentage(추첨 시점 실효 확률)로 역산한
 * 구간별 [설정 확률 ↔ 실제 발급률]을 보여준다.
 *
 * 근거 = _ai-runs/2026-07-28_003_roulette_probability_audit/
 * 대응 절차 = _handover/10_RUNBOOKS.md §12
 */
const BenefitProbabilitySegments = ({projectId}) => {
    const [data, setData] = useState(null)

    useEffect(() => {
        getDetailBenefitProbabilitySegments(projectId)
            .then(({data}) => setData(data))
            .catch(() => setData(null))
    }, [projectId]);

    if (!data) return null

    const {changed, approximated, segmentCount, segments, anomalies, currentSettings} = data

    // 변경도 없고 특이 발급도 없으면 굳이 노출하지 않는다(기존 화면과 동일한 상황).
    if (!changed && (!anomalies || anomalies.length === 0)) return null

    const settingOf = (benefitId) =>
        currentSettings?.find(s => Number(s.benefitId) === Number(benefitId))

    return (
        <div className="flex-col gap-4 border rounded-lg" style={{padding: "16px", background: "#FFFBEB", borderColor: "#FDE68A"}}>
            {
                changed && (
                    <div className="flex items-start gap-2">
                        <AlertTriangle className="w-5 h-5 shrink-0" style={{color: "#D97706"}} />
                        <div className="flex-col gap-1">
                            <div className="font-bold" style={{color: "#92400E"}}>
                                이 프로젝트는 진행 중 당첨 확률이 변경되었습니다 (구간 {segmentCount}개)
                            </div>
                            <div className="text-sm" style={{color: "#92400E"}}>
                                위 '혜택 성과'의 누적 수치는 <b>변경 전·후가 합산</b>되어 있어 현재 확률과 맞지 않는 것이 정상입니다.
                                아래 구간별 수치로 비교해 주세요.
                            </div>
                        </div>
                    </div>
                )
            }

            {
                approximated && (
                    <div className="flex items-start gap-2 text-sm" style={{color: "#92400E"}}>
                        <Info className="w-4 h-4 shrink-0" />
                        <div>
                            발급량이 많아 구간을 <b>근사 계산</b>했습니다. 확률이 바뀌는 시점에 걸친 발급 건이
                            앞 구간에 몰려 계상되므로, 구간별 건수는 참고용으로만 보세요.
                        </div>
                    </div>
                )
            }

            {
                segments?.map((segment) => (
                    <div key={segment.index} className="border rounded-lg bg-white" style={{padding: "12px"}}>
                        <div className="flex-align-center justify-between mb-2">
                            <div className="font-bold">
                                구간 {segment.index}
                                <span className="text-grey-4 font-normal" style={{marginLeft: "8px"}}>
                                    {segment.fromAt} ~ {segment.toAt}
                                </span>
                            </div>
                            <div className="text-sm text-grey-4">발급 {segment.totalIssued.toLocaleString()}건</div>
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-grey-4" style={{borderBottom: "1px solid var(--grey-1)"}}>
                                    <th className="text-left" style={{padding: "6px 4px"}}>혜택</th>
                                    <th className="text-right" style={{padding: "6px 4px"}}>구간 설정 확률</th>
                                    <th className="text-right" style={{padding: "6px 4px"}}>실제 발급</th>
                                    <th className="text-right" style={{padding: "6px 4px"}}>실제 비율</th>
                                    <th className="text-right" style={{padding: "6px 4px"}}>현재 설정</th>
                                </tr>
                            </thead>
                            <tbody>
                            {
                                segment.benefits.map((benefit) => {
                                    const current = settingOf(benefit.benefitId)
                                    const currentPercent = current?.winningPercent
                                    const differsFromCurrent = currentPercent !== null && currentPercent !== undefined
                                        && Math.abs(Number(currentPercent) - benefit.effectivePercent) >= 0.001
                                    return (
                                        <tr key={benefit.benefitId} style={{borderBottom: "1px solid var(--grey-0)"}}>
                                            <td style={{padding: "6px 4px"}}>{benefit.name ?? `#${benefit.benefitId}`}</td>
                                            <td className="text-right font-bold" style={{padding: "6px 4px"}}>
                                                {benefit.effectivePercent}%
                                            </td>
                                            <td className="text-right" style={{padding: "6px 4px"}}>
                                                {benefit.issued.toLocaleString()}건
                                            </td>
                                            <td className="text-right" style={{padding: "6px 4px"}}>
                                                {benefit.actualPercent}%
                                            </td>
                                            <td className="text-right" style={{padding: "6px 4px"}}>
                                                <span className={differsFromCurrent ? "text-red-0 font-bold" : "text-grey-4"}>
                                                    {currentPercent ?? "-"}{currentPercent !== null && currentPercent !== undefined ? "%" : ""}
                                                </span>
                                            </td>
                                        </tr>
                                    )
                                })
                            }
                            </tbody>
                        </table>
                        {
                            segment.totalIssued < 50 && (
                                <div className="flex items-center gap-1 text-sm text-grey-4" style={{marginTop: "8px"}}>
                                    <Info className="w-3 h-3 shrink-0" />
                                    발급 {segment.totalIssued}건은 표본이 작아 실제 비율이 설정 확률과 차이 나는 것이 정상입니다
                                    (50건 이상부터 비교 권장).
                                </div>
                            )
                        }
                    </div>
                ))
            }

            {
                anomalies?.length > 0 && (
                    <div className="border rounded-lg bg-white" style={{padding: "12px"}}>
                        <div className="font-bold mb-2">확률 추첨 외 발급</div>
                        <div className="flex-col gap-2">
                            {
                                anomalies.map((anomaly, index) => (
                                    <div key={index} className="flex-col gap-1 text-sm">
                                        <div className="flex-align-center gap-2">
                                            <div className="text-primary font-bold">[{anomaly.typeKor}]</div>
                                            <div>{anomaly.name ?? `#${anomaly.benefitId}`}</div>
                                            <div className="font-bold">{anomaly.issued.toLocaleString()}건</div>
                                            <div className="text-grey-4">{anomaly.fromAt} ~ {anomaly.toAt}</div>
                                        </div>
                                        <div className="text-grey-4">{anomaly.description}</div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                )
            }
        </div>
    )
}

export default BenefitProbabilitySegments
